import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { lazy, useEffect, useState } from "react";
import {
  I18nextProvider,
  withTranslation,
  WithTranslation,
} from "react-i18next";
import { BorosThemeProvider } from "boros";
import { StylesProvider } from "@material-ui/core/styles";
//import LiveInterview from "./FLASH-Hire/components/LiveInterview";
import { Route } from "react-router-dom";
import { BrowserRouter, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store";
import { CONSTANTS } from "./config/constants";
import PublicRoute from "./Common/utils/publicRoute";
import PrivateRoute from "./Common/utils/privateRoute";
import {
  ACCESS_TOKEN,
  getToken,
  LANGUAGE_TOKEN,
} from "./Common/utils/tokenUtils";
import { getProfile } from "./api_factory/user";
import React from "react";
import i18n, { changeI18nLanguage } from "./Common/i18n";
import {
  FORGOT_PASSWORD_PATH,
  LOGIN_PATH,
  VERIFY_TOKEN,
  CANDIDATE_LOGIN_PATH,
  JOB_EXPIRED,
  RECRUITER_LOGIN_PATH,
  MAINTENANCE_PATH,
  LOGIN_PATH_DEV,
  CANDIDATE_LOGIN_PATH_DEV,
  RECRUITER_LOGIN_PATH_DEV,
} from "./Authentication/constants/NavigationConstants";
import MaintenancePage from "./Authentication/routes/MaintenancePage";
import { routeToChangePassword } from "./Authentication/utils/AuthUtils";

export const AppContext = React.createContext({});

// Lazy loading components

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./Authentication/routes/LoginPage"));
const AutoLoginUser = lazy(
  () => import("./Authentication/routes/AutoLoginUser")
);
const LiveInterview = lazy(() => import("./LiveInterview"));
const PasswordReset = lazy(
  () => import("./Authentication/routes/ResetPassword")
);
const ForgotPassword = lazy(
  () => import("./Authentication/routes/ForgotPassword/index")
);

const NewLoginPage = lazy(
  () => import("./Authentication/routes/NewLoginPage/index")
);
const JobExpire = lazy(() => import("./Authentication/routes/JobExpire/index"));
// const PrivateRoute = lazy(() => import("./Common/utils/privateRoute"));
// const PublicRoute = lazy(() => import("./Common/utils/publicRoute"));
// const LoadingModal = lazy(() => import("./Common/components/LoadingModal"));

interface AppPropTypes extends WithTranslation {}

function App(props: AppPropTypes) {
  const [user, setUser] = useState<any>({});
  const [alreadyRendered, setAlreadyRendered] = useState(false);
  useEffect(() => {
    console.log(process.env, CONSTANTS);
    let accessToken = getToken(ACCESS_TOKEN);
    if (!accessToken) {
      return;
    }
    const hireHostName = window.location.hostname;
    const isRecruiterDomain =
      hireHostName && hireHostName.includes("recruiter") ? "yes" : "no";
    getProfile(isRecruiterDomain)
      .then((res: any) => {
        setUser(res.data);
        let userLanguage = getToken(LANGUAGE_TOKEN);
        if (!userLanguage) changeI18nLanguage(res.data.language);
        if (!res.data?.isPasswordValid) {
          routeToChangePassword(res.data.message);
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const theme = {
    palette: {
      primary: {
        main: "#7050EB",
        50: "#FEF3F4",
        100: "#FBD2D3",
        200: "#F7A5A8",
        300: "#F2777C",
        400: "#EE4A51",
        500: "#EA1D25",
        600: "#BB171E",
        700: "#8C1116",
        800: "#5E0C0F",
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#FDB51B",
        50: "#FFFBF3",
        100: "#FFF0D1",
        200: "#FEE1A4",
        300: "#FED376",
        400: "#FDC449",
        500: "#FDB51B",
        600: "#CA9116",
        700: "#986D10",
        800: "#65480B",
        contrastText: "#ffffff",
      },
      /**
       * @property {Object} orange
       */
      orange: {
        main: "#F17624",
        50: "#FEF8F4",
        100: "#FCE4D3",
        200: "#F9C8A7",
        300: "#F7AD7C",
        400: "#F49150",
        500: "#F17624",
        600: "#C15E1D",
        700: "#914716",
        800: "#602F0E",
        contrastText: "#ffffff",
      },
      grey: {
        300: "#F5F6F8",
        400: "#E3E3E3",
        500: "#CCCCCC",
        600: "#888888",
        700: "#666666",
        800: "#333333",
        900: "#000000",
        contrastText: "#ffffff",
      },
      sideBarColor: {
        main: "#EDEEEE",
      },
      sideBarTextColor: {
        main: "#757575",
      },
      sideBarActiveItem: {
        main: "#fdb51b",
        contrastColor: "#f7cb6f",
      },
      text: {
        primary: "#333333",
        secondary: "#888888",
      },
      placeholder: {},
    },
    typography: {
      button: {
        fontSize: "1rem",
        textTransform: "none",
        lineHeight: "normal",
      },
    },
    shape: {
      borderRadius: 10,
    },
  };
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <StylesProvider injectFirst>
          <BorosThemeProvider theme={theme}>
            <Provider store={store}>
              <PersistGate persistor={persistor}>
                <Switch>
                  <PublicRoute
                    exact
                    path={LOGIN_PATH}
                    component={(props: any) => (
                      <Login {...props} setUser={setUser} />
                    )}
                  />
                  <PublicRoute
                    exact
                    path={MAINTENANCE_PATH}
                    component={(props: any) => <MaintenancePage {...props} />}
                  ></PublicRoute>
                  <PublicRoute
                    exact
                    path={FORGOT_PASSWORD_PATH}
                    component={ForgotPassword}
                  />
                  <PublicRoute
                    exact
                    path={CANDIDATE_LOGIN_PATH}
                    component={(props: any) => (
                      <NewLoginPage
                        {...props}
                        setUser={setUser}
                        key="candidate-login"
                      />
                    )}
                  />
                  <PublicRoute
                    exact
                    path={RECRUITER_LOGIN_PATH}
                    component={(props: any) => (
                      <NewLoginPage
                        {...props}
                        setUser={setUser}
                        key="recruiter-login"
                      />
                    )}
                  />
                  <PublicRoute
                    exact
                    path={JOB_EXPIRED}
                    component={(props: any) => (
                      <JobExpire {...props} key="job-expired" />
                    )}
                  />
                  <PublicRoute
                    exact
                    path={VERIFY_TOKEN}
                    component={(props: any) => (
                      <AutoLoginUser
                        key="login"
                        fromApp={alreadyRendered}
                        {...props}
                        setUser={setUser}
                        setAlreadyRendered={setAlreadyRendered}
                      />
                    )}
                  />
                  <PublicRoute
                    exact
                    path={CONSTANTS.PASSWORD_RESET_FORM_PATH}
                    component={PasswordReset}
                  />
                  {/* <Route exact path="/liveinterview" component={LiveInterview} /> */}
                  {/* <Route exact path="/liveinterview/room/:URLRoomName" component={LiveInterview} /> */}
                  <Route
                    exact
                    path="/liveinterview"
                    component={LiveInterview}
                  />

                  <AppContext.Provider value={user}>
                    <PrivateRoute path="/" component={Home} />
                  </AppContext.Provider>
                </Switch>
              </PersistGate>
            </Provider>
          </BorosThemeProvider>
        </StylesProvider>
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default withTranslation()(App);
